import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback } from 'react';
import styled from 'styled-components';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/components/Select';
import { Text } from 'foundations-components/components/Text';
import { GenericSkeleton } from '../../../components/GenericSkeleton';
import { View as FoundationsView } from 'foundations-components/components/View';
import { getCheckedOptionFromView, TEAM } from '../../getCheckedOptionFromView';
import { useAllTeams } from '../../useAllTeams';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EMPTY_STATE = `--`;
const TeamDescription = () => /*#__PURE__*/_jsx(Text, {
  typography: "detail-200",
  color: "text-core-subtle",
  marginBlockStart: "200",
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "viewsManagement.modals.manageSharing.myTeamDescription"
  })
});
export const TeamRadioOptionPresentational = ({
  teams,
  originalView,
  viewUpdates,
  setViewUpdates,
  isCurrentValueCorrupted,
  hasSharedViewPermission = true,
  customSharedViewPermissionErrorMessage
}) => {
  const currentDraftView = Object.assign({}, originalView, viewUpdates);

  //When we load up this radio button in an invalid state (such as if the current team is deleted), we want it to show up as unchecked
  const isChecked = getCheckedOptionFromView(currentDraftView) === TEAM && !isCurrentValueCorrupted;
  const teamOnView = teams.find(team => team.id === originalView.teamId);
  const teamsUserIsOn = teams.filter(team => team.isUserOnTeam);
  const possibleTeamsToShareThisWith = Array.from(new Set([teamOnView, ...teamsUserIsOn].filter(x => x !== undefined)));
  const onTeamChange = useCallback(teamId => {
    setViewUpdates({
      private: true,
      teamId
    });
  }, [setViewUpdates]);
  if (!hasSharedViewPermission) {
    return /*#__PURE__*/_jsx(Tooltip, {
      title: customSharedViewPermissionErrorMessage || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.errors.publicPermissionRequired"
      }),
      children: /*#__PURE__*/_jsxs(RadioInput, {
        disabled: true,
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "viewsManagement.modals.manageSharing.myTeam"
        }), /*#__PURE__*/_jsx(TeamDescription, {})]
      })
    });
  }

  /**
   * If there are no possible teams to assign this to, render a disabled radio button
   */
  if (possibleTeamsToShareThisWith.length === 0) {
    return /*#__PURE__*/_jsxs(RadioInput, {
      disabled: true,
      "data-test-id": `view-sharing-team-checked=${false}`,
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.myTeam"
      }), /*#__PURE__*/_jsx(TeamDescription, {}), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.errors.noPossibleTeamsToShareWith"
      })]
    });
  }

  /*
   * If there is only one team to assign this to, render a button with the team name next to it
   */
  const firstPossibleTeam = possibleTeamsToShareThisWith[0];
  if (possibleTeamsToShareThisWith.length === 1) {
    return /*#__PURE__*/_jsxs(RadioInput, {
      checked: isChecked,
      value: firstPossibleTeam.id,
      onChange: e => onTeamChange(Number(e.target.value)),
      "data-test-id": `view-sharing-team-checked=${isChecked}`,
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.myTeam"
      }), isChecked && firstPossibleTeam.name, !isChecked && EMPTY_STATE, /*#__PURE__*/_jsx(TeamDescription, {})]
    });
  }
  const optionsFormattedForUISelect = possibleTeamsToShareThisWith.map(({
    id,
    name,
    isUserOnTeam
  }) => {
    return {
      value: id,
      text: name,
      help: !isUserOnTeam && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.notOnThisTeam"
      })
    };
  });

  /** If there is more than one team to choose from, render a select
   */
  return /*#__PURE__*/_jsxs(RadioInput, {
    "data-test-id": `view-sharing-team-checked=${isChecked}`,
    checked: isChecked,
    value: firstPossibleTeam.id
    /**
     * This onChange is different than UISelect's onChange
     *
     * This onChange is triggered when the user clicks on the My Team radio option (if its enabled)
     * At this point, we need to set `teamId` to _something_, so we default to the first possible team
     *
     * After that initial value is set, the user can then click into the input and select a different one if they prefer,
     * (which will be when UISelect.onChange is called)
     * */,
    onChange: e => onTeamChange(Number(e.target.value)),
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "viewsManagement.modals.manageSharing.myTeam"
    }), /*#__PURE__*/_jsx(TeamDescription, {}), /*#__PURE__*/_jsx(FoundationsView, {
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(Select, {
        "data-test-id": "view-sharing-editor-team-select",
        options: optionsFormattedForUISelect,
        selectedValues: currentDraftView.teamId ? [currentDraftView.teamId] : [],
        onSelectedOptionChange: e => onTeamChange(e.selectedValues[0]),
        disabled: !isChecked
      })
    })]
  });
};
const StyledSkeletonRadioInput = styled(GenericSkeleton).withConfig({
  displayName: "TeamRadioOption__StyledSkeletonRadioInput"
})(["height:24px;width:100px;"]);
export const TeamRadioOption = props => {
  const {
    loading,
    error,
    data: teams
  } = useAllTeams();
  if (loading) {
    return /*#__PURE__*/_jsx(RadioInput, {
      disabled: true,
      children: /*#__PURE__*/_jsx(StyledSkeletonRadioInput, {})
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(RadioInput, {
      disabled: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.errors.teamFetchingError"
      })
    });
  }
  const currentTeamId = props.viewUpdates.teamId || props.originalView.teamId;
  const doesCurrentTeamIdEvenExist = Boolean(teams.find(team => team.id === currentTeamId));
  const isCurrentValueCorrupted = currentTeamId !== undefined && !doesCurrentTeamIdEvenExist;
  const valueIsCorruptedWarning = isCurrentValueCorrupted && /*#__PURE__*/_jsx(FormattedMessage, {
    message: "viewsManagement.modals.manageSharing.errors.teamNoLongerExists"
  });
  return /*#__PURE__*/_jsx(FormControl, {
    error: isCurrentValueCorrupted,
    validationMessage: valueIsCorruptedWarning,
    children: /*#__PURE__*/_jsx(TeamRadioOptionPresentational, Object.assign({
      teams: teams,
      isCurrentValueCorrupted: isCurrentValueCorrupted
    }, props))
  });
};